import React from 'react';
import CrtTitle from "./crtTitle";
import './App.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExternalLinkAlt, faChevronRight, faEye, faVolumeUp, faEnvelope } from '@fortawesome/free-solid-svg-icons'
import AvariceLogo from "./assets/avarice-logo.png";
import AnarchyLogo from "./assets/anarchy-ims-logo.svg";
import Konjur3DLogo from "./assets/konjur3d-logo.png";

// Left-hugging navigation for the introduction sections
const INTRO_TOC_SECTIONS = [
  { id: "intro-overview", label: "OVERVIEW" },
  { id: "intro-avarice", label: "AVARICE ERP" },
  { id: "intro-universe", label: "ANARCHY IMS" },
  { id: "intro-konjur", label: "KONJUR3D" },
];

const ProductReticle = ({ coordinates }) => (
  <div className="product-reticle" aria-hidden="true">
    <span className="product-reticle-corner product-reticle-corner-tl"></span>
    <span className="product-reticle-corner product-reticle-corner-tr"></span>
    <span className="product-reticle-corner product-reticle-corner-bl"></span>
    <span className="product-reticle-corner product-reticle-corner-br"></span>
    <span className="product-reticle-ring"></span>
    <span className="product-reticle-sweep"></span>
    <span className="product-reticle-crosshair"></span>
    <span className="product-reticle-lock"></span>
    <span className="product-reticle-status">
      <span><FontAwesomeIcon icon={faVolumeUp} /> AUDIO</span>
      <span><FontAwesomeIcon icon={faEye} /> VISUAL</span>
    </span>
    <span className="product-reticle-coordinates">GPS {coordinates}</span>
  </div>
);

class Introduction extends React.Component {
  constructor(props) {
    super(props);

    // Generate tree graph structure
    this.treeGraph = this.generateTreeGraph();

    this.state = {
      activeSection: INTRO_TOC_SECTIONS[0].id
    };

    // Viewport state lives outside React state: the camera mutates the SVG
    // attributes directly each tick so the whole tree isn't re-rendered
    this.anim = {
      viewportX: 0,
      viewportY: 0,
      currentEdgeIndex: 0,
      animationProgress: 0
    };

    this.svgRef = React.createRef();
    this.crosshairRef = React.createRef();

    this.animationFrameId = null;
    this.animationSpeed = 0.0001; // Speed of movement along edge — slow drift, ~3 min per edge

    this.handleScroll = this.handleScroll.bind(this);
  }

  handleScroll() {
    // Highlight the last section whose top has passed 40% of the viewport
    const threshold = window.innerHeight * 0.4;
    let active = INTRO_TOC_SECTIONS[0].id;

    INTRO_TOC_SECTIONS.forEach(section => {
      const el = document.getElementById(section.id);
      if (el && el.getBoundingClientRect().top <= threshold) {
        active = section.id;
      }
    });

    if (active !== this.state.activeSection) {
      this.setState({ activeSection: active });
    }
  }

  scrollToSection(id) {
    const el = document.getElementById(id);
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    }
  }

  generateTreeGraph() {
    // Generate a large tree graph structure
    const nodes = [];
    const edges = [];
    const levels = 8;
    const nodesPerLevel = [1, 3, 5, 8, 12, 18, 25, 35];
    const graphWidth = 5000;
    const graphHeight = 4000;
    
    let nodeId = 0;
    const levelHeight = graphHeight / levels;
    
    // Create nodes
    for (let level = 0; level < levels; level++) {
      const count = nodesPerLevel[level];
      const levelY = level * levelHeight + 200;
      
      for (let i = 0; i < count; i++) {
        // Scatter nodes widely around their slot so levels don't read as rows
        const spacing = graphWidth / (count + 1);
        const x = spacing * (i + 1) + (Math.random() - 0.5) * spacing * 0.9;
        const y = levelY + (Math.random() - 0.5) * levelHeight * 0.85;
        // Generate random byte code string (8-16 bytes)
        const byteCount = Math.floor(Math.random() * 9) + 8;
        const byteCode = Array.from({length: byteCount}, () => {
          return Math.floor(Math.random() * 256).toString(16).padStart(2, '0').toUpperCase();
        }).join(' ');
        nodes.push({ id: nodeId++, x, y, level, byteCode });
      }
    }
    
    // Create edges (connect each node to 1-3 random nodes in next level)
    let nodeIndex = 0;
    for (let level = 0; level < levels - 1; level++) {
      const currentLevelCount = nodesPerLevel[level];
      const nextLevelCount = nodesPerLevel[level + 1];
      const currentLevelNodes = nodes.slice(nodeIndex, nodeIndex + currentLevelCount);
      const nextLevelNodes = nodes.slice(nodeIndex + currentLevelCount, nodeIndex + currentLevelCount + nextLevelCount);
      
      currentLevelNodes.forEach(node => {
        const connections = Math.floor(Math.random() * 3) + 1;
        const used = new Set();
        for (let i = 0; i < connections && i < nextLevelNodes.length; i++) {
          let targetIndex;
          do {
            targetIndex = Math.floor(Math.random() * nextLevelNodes.length);
          } while (used.has(targetIndex));
          used.add(targetIndex);
          edges.push({
            from: node.id,
            to: nextLevelNodes[targetIndex].id,
            fromNode: node,
            toNode: nextLevelNodes[targetIndex]
          });
        }
      });
      
      nodeIndex += currentLevelCount;
    }
    
    // Add random blue nodes with IP addresses
    const blueNodes = [];
    const blueNodeCount = Math.floor(nodes.length * 0.3); // 30% of total nodes are blue
    for (let i = 0; i < blueNodeCount; i++) {
      const x = Math.random() * graphWidth;
      const y = Math.random() * graphHeight;
      // Generate random IP address
      const ipAddress = `${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}`;
      blueNodes.push({ 
        id: `blue-${i}`, 
        x, 
        y, 
        ipAddress,
        isBlue: true 
      });
    }
    
    // Add random red ghost nodes with threat hex codes
    const ghostNodes = [];
    const ghostCount = Math.floor(nodes.length * 0.2); // 20% of total nodes are ghosts
    for (let i = 0; i < ghostCount; i++) {
      const x = Math.random() * graphWidth;
      const y = Math.random() * graphHeight;
      // Generate random hex code (4-8 hex digits)
      const hexLength = Math.floor(Math.random() * 5) + 4;
      const hexCode = Array.from({length: hexLength}, () => {
        return Math.floor(Math.random() * 16).toString(16).toUpperCase();
      }).join('');
      ghostNodes.push({ 
        id: `ghost-${i}`, 
        x, 
        y, 
        hexCode,
        isGhost: true 
      });
    }
    
    return { nodes, edges, blueNodes, ghostNodes, width: graphWidth, height: graphHeight };
  }
  
  componentDidMount() {
    this.startAnimation();

    window.addEventListener('scroll', this.handleScroll, { passive: true });
    this.handleScroll();
  }

  componentWillUnmount() {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }

    window.removeEventListener('scroll', this.handleScroll);
  }

  computeViewBox() {
    const zoom = 0.9; // Zoom factor - below 1 for a more distant view
    const viewportWidth = window.innerWidth || 1920;
    const viewportHeight = window.innerHeight || 1080;

    const viewBoxX = this.anim.viewportX - viewportWidth / (2 * zoom);
    const viewBoxY = this.anim.viewportY - viewportHeight / (2 * zoom);
    const viewBoxWidth = viewportWidth / zoom;
    const viewBoxHeight = viewportHeight / zoom;

    return `${viewBoxX} ${viewBoxY} ${viewBoxWidth} ${viewBoxHeight}`;
  }

  startAnimation() {
    // Update every 3rd frame (~20fps) — plenty for a slow, blurred drift — and
    // write the SVG attributes directly instead of re-rendering through React
    const FRAME_SKIP = 3;
    let frameCount = 0;

    const animate = () => {
      frameCount++;

      if (frameCount % FRAME_SKIP === 0) {
        const anim = this.anim;

        // Move along current edge
        anim.animationProgress += this.animationSpeed * FRAME_SKIP;

        if (anim.animationProgress >= 1) {
          // Reached end of edge, pick a random next edge
          const currentEdge = this.treeGraph.edges[anim.currentEdgeIndex];
          const currentNodeId = currentEdge.to;

          // Find edges starting from current node
          const nextEdges = this.treeGraph.edges.filter(e => e.from === currentNodeId);

          if (nextEdges.length > 0) {
            anim.currentEdgeIndex = this.treeGraph.edges.indexOf(
              nextEdges[Math.floor(Math.random() * nextEdges.length)]
            );
          } else {
            // No outgoing edges, pick a random edge
            anim.currentEdgeIndex = Math.floor(Math.random() * this.treeGraph.edges.length);
          }
          anim.animationProgress = 0;
        }

        // Calculate viewport position along edge
        const edge = this.treeGraph.edges[anim.currentEdgeIndex];
        const t = anim.animationProgress;
        anim.viewportX = edge.fromNode.x * (1 - t) + edge.toNode.x * t;
        anim.viewportY = edge.fromNode.y * (1 - t) + edge.toNode.y * t;

        if (this.svgRef.current) {
          this.svgRef.current.setAttribute('viewBox', this.computeViewBox());
        }
        if (this.crosshairRef.current) {
          this.crosshairRef.current.setAttribute('transform', `translate(${anim.viewportX}, ${anim.viewportY})`);
        }
      }

      this.animationFrameId = requestAnimationFrame(animate);
    };

    // Start with a random edge
    this.anim.currentEdgeIndex = Math.floor(Math.random() * this.treeGraph.edges.length);

    animate();
  }
  
  render() {
    return (
      <div>
        <div className="intro-background" style={{position: "fixed", top: 0, left: 0, width: "100%", height: "100vh", overflow: "hidden", zIndex: 0}}>
          <svg
            ref={this.svgRef}
            width="100%"
            height="100%"
            viewBox={this.computeViewBox()}
            style={{position: "absolute", top: 0, left: 0}}
            preserveAspectRatio="xMidYMid slice"
          >
            {/* Geographic boundaries - world map underlay */}
            <image
              href="worldmap.svg"
              x="0"
              y="0"
              width="5000"
              height="4000"
              preserveAspectRatio="none"
              opacity="0.13"
              style={{filter: "sepia(1) saturate(4) hue-rotate(65deg)"}}
            />

            {/* Longitude lines */}
            {Array.from({length: 15}, (_, k) => (
              <line
                key={`lon-${k}`}
                x1={-1000 + k * 500}
                y1={-800}
                x2={-1000 + k * 500}
                y2={4800}
                stroke="#00ff00"
                strokeWidth="0.5"
                opacity="0.12"
              />
            ))}

            {/* Latitude lines */}
            {Array.from({length: 15}, (_, k) => (
              <line
                key={`lat-${k}`}
                x1={-1000}
                y1={-800 + k * 400}
                x2={6000}
                y2={-800 + k * 400}
                stroke="#00ff00"
                strokeWidth="0.5"
                opacity="0.12"
              />
            ))}

            {/* Render edges */}
            {this.treeGraph.edges.map((edge, i) => (
              <line
                key={`edge-${i}`}
                x1={edge.fromNode.x}
                y1={edge.fromNode.y}
                x2={edge.toNode.x}
                y2={edge.toNode.y}
                stroke="#00ff00"
                strokeWidth="0.6"
                opacity="0.25"
              />
            ))}
            
            {/* Render green nodes */}
            {this.treeGraph.nodes.map((node) => (
              <g key={`node-${node.id}`}>
                <circle
                  cx={node.x}
                  cy={node.y}
                  r="8"
                  fill="#00ff00"
                  opacity="0.6"
                />
                <circle
                  cx={node.x}
                  cy={node.y}
                  r="12"
                  stroke="#00ff00"
                  strokeWidth="0.6"
                  fill="#00000000"
                  opacity="0.6"
                />
                <text
                  x={node.x}
                  y={node.y - 15}
                  fill="#00ff00"
                  fontSize="12"
                  fontFamily="monospace"
                  textAnchor="middle"
                  opacity="0.8"
                >
                  {node.byteCode}
                </text>
              </g>
            ))}
            
            {/* Render blue nodes with IP addresses */}
            {this.treeGraph.blueNodes.map((node) => (
              <g key={`blue-node-${node.id}`}>
                <circle
                  cx={node.x}
                  cy={node.y}
                  r="8"
                  fill="#0088ff"
                  opacity="0.6"
                />
                <circle
                  cx={node.x}
                  cy={node.y}
                  r="12"
                  stroke="#0088ff"
                  strokeWidth="0.6"
                  fill="#00000000"
                  opacity="0.6"
                />
                <text
                  x={node.x}
                  y={node.y - 15}
                  fill="#0088ff"
                  fontSize="12"
                  fontFamily="monospace"
                  textAnchor="middle"
                  opacity="0.8"
                >
                  {node.ipAddress}
                </text>
              </g>
            ))}
            
            {/* Render red spinning crosses with threat hex codes */}
            {this.treeGraph.ghostNodes.map((node) => (
              <g key={`ghost-${node.id}`} transform={`translate(${node.x}, ${node.y})`}>
                {/* Spinning red cross */}
                <g>
                  <animateTransform
                    attributeName="transform"
                    type="rotate"
                    values="0;360"
                    dur="2s"
                    repeatCount="indefinite"
                  />
                  {/* Vertical bar */}
                  <rect x="-0.5" y="-20" width="1" height="40" fill="#ff0000" opacity="0.9" />
                  {/* Horizontal bar */}
                  <rect x="-20" y="-0.5" width="40" height="1" fill="#ff0000" opacity="0.9" />
                </g>
                {/* Threat text */}
                <text
                  x="0"
                  y="30"
                  fill="#ff0000"
                  fontSize="10"
                  fontFamily="monospace"
                  textAnchor="middle"
                  opacity="0.9"
                  fontWeight="bold"
                >
                  THREAT - 0x{node.hexCode}
                </text>
              </g>
            ))}
            
            {/* Crosshair at center of viewport */}
            <g ref={this.crosshairRef} transform={`translate(${this.anim.viewportX}, ${this.anim.viewportY})`}>
              <g>
                <animateTransform
                  attributeName="transform"
                  type="scale"
                  values="1;1.3;1"
                  dur="3s"
                  repeatCount="indefinite"
                />
                <line
                  x1="-30"
                  y1="0"
                  x2="30"
                  y2="0"
                  stroke="#00ff0088"
                  strokeWidth="1"
                  opacity="0.8"
                />
                <line
                  x1="0"
                  y1="-30"
                  x2="0"
                  y2="30"
                  stroke="#00ff0088"
                  strokeWidth="1"
                  opacity="0.8"
                />
                <circle
                  cx="0"
                  cy="0"
                  r="4"
                  fill="none"
                  stroke="#00ff0088"
                  strokeWidth="1"
                  opacity="0.8"
                />
              </g>
            </g>
          </svg>
        </div>

        <div className="intro-shield">
            { /* 
          <div style={{float: "right", fontSize: "0.4em", color: "#FFFFFF !important"}}>{ Date.now() }</div>

          <div style={{ position: "absolute", top: "170px", left: "200px", width: "3px", height: "2px", animation: "blink1 1s infinite"}}></div>
          <div style={{ position: "absolute", top: "175px", left: "200px", width: "3px", height: "2px", background: "#FF0000"}}></div>
          <div style={{ position: "absolute", top: "220px", left: "200px", width: "3px", height: "2px", background: "#FF0000"}}></div>
          <div style={{ position: "absolute", top: "175px", left: "520px", width: "3px", height: "2px", background: "#FF0000"}}></div>
          <div style={{ position: "absolute", top: "220px", left: "520px", width: "3px", height: "2px", background: "#FF0000"}}></div>
          <div style={{ position: "absolute", top: "225px", left: "520px", width: "3px", height: "2px", animation: "blink2 1s infinite"}}></div>
          <div style={{ position: "absolute", top: "480px", left: "900px", width: "3px", height: "2px", animation: "blink5 1s infinite"}}></div>
          <div style={{ position: "absolute", top: "480px", left: "920px", width: "3px", height: "2px", animation: "blink3 1s infinite"}}></div>
          <div style={{ position: "absolute", top: "480px", left: "930px", width: "3px", height: "2px", animation: "blink4 1s infinite"}}></div>
          <div style={{ position: "absolute", top: "480px", left: "950px", width: "3px", height: "2px", animation: "blink1 1s infinite"}}></div>
          <div style={{ position: "absolute", top: "540px", left: "900px", width: "3px", height: "2px", animation: "blink1 1s infinite"}}></div>
          <div style={{ position: "absolute", top: "540px", left: "920px", width: "3px", height: "2px", animation: "blink5 1s infinite"}}></div>
          <div style={{ position: "absolute", top: "540px", left: "930px", width: "3px", height: "2px", animation: "blink6 1s infinite"}}></div>
          <div style={{ position: "absolute", top: "540px", left: "950px", width: "3px", height: "2px", animation: "blink3 1s infinite"}}></div>
          */ }
        </div> 

        { /* Left-hugging section navigation */ }
        <nav className="advisory-toc noselect" aria-label="Introduction sections">
          {INTRO_TOC_SECTIONS.map(section => (
            <button
              type="button"
              key={section.id}
              className={`advisory-toc-item ${this.state.activeSection === section.id ? "advisory-toc-active" : ""}`}
              onClick={() => this.scrollToSection(section.id)}
              aria-current={this.state.activeSection === section.id ? 'location' : undefined}
            >
              {section.label}
            </button>
          ))}
          <a
            href="/advisory"
            className="advisory-toc-item advisory-toc-go advisory-toc-go-bottom"
            onClick={(event) => this.props.onNavigate && this.props.onNavigate('advisory', event)}
          >
            ADVISORY&nbsp;&rsaquo;
          </a>
        </nav>

        <div className="content" style={{ marginTop: "50px", minHeight: "60vh", paddingBottom: "80px"}}>

          <CrtTitle text="RELENTLESS" style={{ marginBottom: "50px" }} />

          <div className="page-body">
            {/* Intro description - 0.8em x 0.8em matches the product summary text exactly */}
            <div id="intro-overview" className="intro-column advisory-section" style={{marginBottom: "70px", background: "rgba(0, 0, 0, 0.75)", padding: "25px 30px", borderRadius: "10px", fontSize: "0.64em", lineHeight: "1.7em", color: "#CCCCCC"}}>
              Relentless is a software business solution provider with a strong background in risk.
              We develop systems that enable management to control the business effectively and measure it transparently.
              We have more than a decade of experience in consulting and providing solutions in government and commerce.
              Our flagship ERP, <strong style={{color: "#FFFFFF"}}>Avarice</strong>, is used across many clients in many industries from manufacture
              to government services and covers most key functions of the larger platforms.
              <span style={{display: "block", marginTop: "16px"}}>
                That background in risk runs deeper than software.  We advise institutions operating in complex risk
                environments where local politics, regulatory theatres and commercial interests intersect, and our
                systems then automate the execution of risk management once policies are in place.
              </span>
              <span style={{display: "block", marginTop: "16px"}}>
                <a href="/advisory" className="advisory-link" onClick={(event) => this.props.onNavigate && this.props.onNavigate('advisory', event)}>
                  <strong style={{color: "#FFFFFF"}}><FontAwesomeIcon icon={faChevronRight} />&nbsp;&nbsp;EXPLORE OUR ADVISORY PRACTICE</strong>
                </a>
              </span>
            </div>

            { /* PRODUCTS INTRO */ }
            <ul className="intro-column" style={{listStyleType: "none", paddingLeft: "0", fontSize: "0.8em"}}>
                <li id="intro-avarice" className="products-intro-hover-item advisory-section" style={{display: "flex", flexWrap: "wrap", alignItems: "flex-start", marginBottom: "60px", background: "rgba(0, 0, 0, 0.75)", padding: "25px 30px", borderRadius: "10px"}}>
                    <div className="logo-container" style={{marginRight: "35px", marginBottom: "38px", flexShrink: 0, position: "relative", width: "80px", height: "80px", display: "flex", alignItems: "center", justifyContent: "center"}}>
                        <ProductReticle coordinates={'25.7461°S 28.1881°E'} />
                        <a href="https://www.avarice.co.za" target="_blank" rel="noreferrer">
                            <img src={AvariceLogo} alt="Avarice ERP" style={{width: "80px", height: "auto"}} />
                        </a>
                    </div>
                    <div style={{flex: "1 1 300px"}}>
                        <h2 className="card-heading">ENTERPRISE RESOURCE PLANNING AND RISK SOLUTIONS</h2>
                        <ul className="products-intro-sublist-hover" style={{listStyleType: "none", paddingLeft: "0", opacity: 1}}>
                            <li className="products-intro-sublist-item" style={{fontSize: "0.8em", lineHeight: "1.7em"}}><a href="https://www.avarice.co.za" target="_blank" rel="noreferrer">
                                <strong>Avarice ERP</strong><span style={{color:'#CCCCCC'}}> - Our flagship ERP system covers most essential functions across most business types: CRM, financial reporting, HR, project management and risk modelling.  
                                Relentless prides itself in delivering world class risk identification and mitigation solutions integrated within our management software.</span>
                                <span style={{display: "block", marginTop: "16px"}}><strong style={{color:'#ffffff'}}><FontAwesomeIcon icon={faExternalLinkAlt} />&nbsp;&nbsp;VISIT WWW.AVARICE.CO.ZA</strong></span></a>
                            </li>
                        </ul>
                    </div>
                </li>
                <li id="intro-universe" className="products-intro-hover-item advisory-section" style={{display: "flex", flexWrap: "wrap", alignItems: "flex-start", marginBottom: "60px", background: "rgba(0, 0, 0, 0.75)", padding: "25px 30px", borderRadius: "10px"}}>
                    <div className="logo-container" style={{marginRight: "35px", marginBottom: "38px", flexShrink: 0, position: "relative", width: "80px", height: "80px", display: "flex", alignItems: "center", justifyContent: "center"}}>
                        <ProductReticle coordinates={'26.2041°S 28.0473°E'} />
                        {/*<a href="https://www.mesoverse.net" target="_blank"> */}
                            <img src={AnarchyLogo} alt="Anarchy IMS" style={{width: "80px", height: "auto"}} />
                        {/*</a>*/}
                    </div>
                    <div style={{flex: "1 1 300px"}}>
                        <h2 className="card-heading">SURVEYING, RETAIL MANAGEMENT AND GIG ECONOMY</h2>
                        <ul className="products-intro-sublist-hover" style={{listStyleType: "none", paddingLeft: "0", opacity: 1}}>
                            <li className="products-intro-sublist-item" style={{fontSize: "0.8em", lineHeight: "1.7em"}}>{/*<a href="https://www.mesoverse.net" target="_blank">*/}
                                <strong style={{color:'#ffffff'}}>Anarchy IMS</strong>{/*</a>*/}<span style={{color:'#CCCCCC'}}>  - Our large scale data collection and boots-on-the-ground management platform has been used to collect millions of data points with complex questionnaires and in difficult environments.
                                Anarchy IMS can digitize a decentralized business across mobile devices and ensure timely operations and cost cutting in management.</span>
                                <span style={{display: "block", marginTop: "16px"}}><a href="/contact" onClick={(event) => this.props.onNavigate && this.props.onNavigate('contact', event)}><strong style={{color:'#ffffff'}}><FontAwesomeIcon icon={faEnvelope} />&nbsp;&nbsp;EMAIL US FOR MORE INFORMATION</strong></a></span>
                            </li>
                        </ul>
                    </div>
                </li>
                <li id="intro-konjur" className="products-intro-hover-item advisory-section" style={{display: "flex", flexWrap: "wrap", alignItems: "flex-start", marginBottom: "60px", background: "rgba(0, 0, 0, 0.75)", padding: "25px 30px", borderRadius: "10px"}}>
                    <div className="logo-container" style={{marginRight: "35px", marginBottom: "38px", flexShrink: 0, position: "relative", width: "80px", height: "80px", display: "flex", alignItems: "center", justifyContent: "center"}}>
                        <ProductReticle coordinates={'33.9249°S 18.4241°E'} />
                        <a href="https://www.konjur3d.com" target="_blank" rel="noreferrer">
                            <img src={Konjur3DLogo} alt="Konjur3D" style={{width: "80px", height: "auto"}} />
                        </a>
                    </div>
                    <div style={{flex: "1 1 300px"}}>
                        <h2 className="card-heading">3D GRAPHICS AND COMPUTER AIDED DESIGN</h2>
                        <ul className="products-intro-sublist-hover" style={{listStyleType: "none", paddingLeft: "0", opacity: 1}}>
                            <li className="products-intro-sublist-item" style={{fontSize: "0.8em", lineHeight: "1.7em"}}><a href="https://www.konjur3d.com" target="_blank" rel="noreferrer">
                                <strong>Konjur3D</strong><span style={{color:'#CCCCCC'}}>  - Our 3D modelling tool for 3D print design, sculpting and general CAD.  
                                Currently, it is only available on iPad and iPhone, but a desktop AI-enabled edition is slated for release in 2026.</span>
                                <span style={{display: "block", marginTop: "16px"}}><strong style={{color:'#ffffff'}}><FontAwesomeIcon icon={faExternalLinkAlt} />&nbsp;&nbsp;VISIT WWW.KONJUR3D.COM</strong></span></a>
                            </li>
                        </ul>
                    </div>
                </li>
            </ul>
          </div>
        </div>
      
        {/* 
        <div className="content" style={{ width: "calc(100vw)", minHeight: "100vh", marginLeft: "0px", paddingTop: "50px", paddingLeft: "100px", paddingRight: "100px", marginRight: "0px", marginTop: "17vh", backgroundColor: "#191919FA", borderRadius: "0px" }}>
          <h2>WHO ARE WE</h2>
          <p>
            Relentless is a information technology business that specializes in business management  solutions integrated with financial and accountings systems.
            We focus on two main areas:  Gig economy and ERP.  Our ERP (Enterprise Resource Planning) engine is used in many types of businesses for 
            procurement, supplier/customer vetting, document processing, accounting and many other things we didn't initially consider.  
          </p>
          <p>
            If you're here for ERP, please follow the link to Avarice ERP our flagship system.
          </p>
          <div style={{ marginBottom: "70px", textAlign: "center", width: "100%" }}><a href="https://www.avarice.co.za" target="_blank"><strong style={{ color: "#FFFFFF"}}><FontAwesomeIcon icon="chevron-right" />&nbsp;&nbsp;Avarice Homepage</strong></a></div>
          <p>
            In 3D graphics we sell an iPad based 3D modelling tool called Konjur3D.  It provides facilities to do hard surface modelling, clay sculpting and texture editting.  The tool is 
            an all in one product and can export to the major standards of STL, OBJ, GLTF and USDZ (for AR applications).  It is available on the Apple Appstore for once off purchase at $15.  
            No subscription frees are required for the base package which includes most modelling functions.  Animation and generative tools are in development for the solution.   
          </p>
          <div style={{ marginBottom: "70px", textAlign: "center", width: "100%" }}><a href="https://www.konjur3d.com" target="_blank"><strong style={{ color: "#FFFFFF"}}><FontAwesomeIcon icon="chevron-right" />&nbsp;&nbsp;Konjur3D Homepage</strong></a></div>

          <p>
            In gig economy we offer Mesoverse, that automates most of the market-making and payment functions necessary in business.  A user can canvas his
            entire business flow and the system will realize this functionality across Android and iOS apps.  The system can be configured for most 
            types of decentralized or centralized business types including retail management, business process outsourcing and car rental.  Trust us, we've done
            the rounds!
          </p>
          <div style={{ marginBottom: "70px", textAlign: "center", width: "100%" }}><a href="https://www.mesoverse.net" target="_blank"><strong style={{ color: "#FFFFFF"}}><FontAwesomeIcon icon="chevron-right" />&nbsp;&nbsp;Mesoverse Homepage</strong></a></div>
          <h4>
            CONSULTING SERVICES
          </h4>
          <p style={{marginBottom: "300px"}}>
            In addition to our products, we also provide finanical, software and engineering services.  Our team
            has enormous experience in areas such as fraud and payment.  We have also developed various solutions
            in more advanced engineering fields such as biometrics, weapons development and sensor design.
          </p>
        </div> */}
      </div>
    );
  }
}

export default Introduction;
